import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CheckCircle, Loader2 } from 'lucide-react';

import { useSubscription } from '@chainlit/react-client';

import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/components/i18n/Translator';

// Poll interval while waiting for the subscription webhook to land in our DB,
// and a hard cap so a stuck/delayed webhook doesn't trap the user here forever.
const POLL_INTERVAL_MS = 1500;
const TIMEOUT_MS = 20000;

// Stripe's pricing table redirects here after a successful checkout (instead
// of straight back to '/') so we can wait for the subscription webhook to
// activate the account before handing the user into the app — landing on '/'
// too early would just bounce them back to /subscribe via RequireSubscription.
export default function PaymentSuccess() {
  const { hasActiveSubscription, refresh } = useSubscription();
  const [timedOut, setTimedOut] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (hasActiveSubscription && !redirectingRef.current) {
      redirectingRef.current = true;
      const redirect = setTimeout(() => navigate('/'), 1200);
      return () => clearTimeout(redirect);
    }
  }, [hasActiveSubscription, navigate]);

  useEffect(() => {
    if (hasActiveSubscription) return;

    const poll = setInterval(() => refresh(), POLL_INTERVAL_MS);
    const timeout = setTimeout(() => setTimedOut(true), TIMEOUT_MS);

    return () => {
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, [hasActiveSubscription, refresh]);

  return (
    <div className="min-h-svh flex flex-col items-center justify-center p-6 text-center">
      <Logo className="w-[150px] mb-10" />

      {hasActiveSubscription ? (
        <>
          <CheckCircle className="h-12 w-12 text-green-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">
            {t('paymentSuccess.successTitle')}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('paymentSuccess.successDescription')}
          </p>
        </>
      ) : timedOut ? (
        <>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('paymentSuccess.timeoutTitle')}
          </h1>
          <p className="text-gray-600 mt-2 max-w-md">
            {t('paymentSuccess.timeoutDescription')}
          </p>
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setTimedOut(false);
                refresh();
              }}
            >
              {t('paymentSuccess.tryAgain')}
            </Button>
            <Button onClick={() => navigate('/')}>
              {t('paymentSuccess.continueAnyway')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-gray-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">
            {t('paymentSuccess.activatingTitle')}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('paymentSuccess.activatingDescription')}
          </p>
        </>
      )}
    </div>
  );
}
